import { AudioManager } from '../audio/AudioManager';
import { TouchInput } from '../input/TouchInput';
import { Renderer } from '../render/Renderer';
import { WEAPONS } from '../data/weapons';
import { applyUpgradeToContext } from '../data/upgrades';
import type {
  BattleOutcome,
  Enemy,
  ForgeModifiers,
  Hero,
  PersistentSave,
  RunState,
  RunSummary,
  Weapon,
} from '../types';
import { ForgePhase } from './phases/ForgePhase';
import { HeroRequestPhase } from './phases/HeroRequestPhase';
import { CombatPhase } from './phases/CombatPhase';
import { ResultPhase } from './phases/ResultPhase';
import { ShopPhase } from './phases/ShopPhase';
import { GameOverPhase } from './phases/GameOverPhase';
import { MetaPhase } from './phases/MetaPhase';
import { generateEnemy } from './systems/EnemyGen';
import { generateHero } from './systems/HeroGen';
import { clampRep, START_REP } from './systems/Reputation';
import { SaveLoad } from './systems/SaveLoad';

const FIXED_DT = 1 / 60;
const MAX_ACCUM = 0.25;
const MAX_HEROES_PER_RUN = 12;

type Stage =
  | { kind: 'meta'; phase: MetaPhase }
  | { kind: 'heroRequest'; phase: HeroRequestPhase; hero: Hero }
  | { kind: 'forge'; phase: ForgePhase; hero: Hero }
  | { kind: 'combat'; phase: CombatPhase; hero: Hero; weapon: Weapon; enemy: Enemy }
  | { kind: 'result'; phase: ResultPhase; outcome: BattleOutcome; hero: Hero }
  | { kind: 'shop'; phase: ShopPhase }
  | { kind: 'gameOver'; phase: GameOverPhase };

function makeForgeModifiers(): ForgeModifiers {
  return {
    heatBandMult: 1,
    strikeWindowMult: 1,
    quenchWindowMult: 1,
    bonusDamage: 0,
    bonusDurability: 0,
    luckyStart: 0,
  };
}

function makeRunState(): RunState {
  return {
    reputation: START_REP,
    deaths: 0,
    gold: 0,
    heroesServed: 0,
    upgrades: [],
    hallOfFameWeapons: [],
    memorial: [],
  };
}

export class Game {
  private renderer: Renderer;
  private input: TouchInput;
  private audio: AudioManager;
  private saveLoad = new SaveLoad();
  private save: PersistentSave;
  private run: RunState | null = null;
  private modifiers: ForgeModifiers = makeForgeModifiers();
  private stage: Stage;
  private last = 0;
  private accum = 0;
  private running = false;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new Renderer(canvas);
    this.input = new TouchInput(this.renderer);
    this.audio = new AudioManager();
    const unbindUnlock = this.input.onPointerDown(() => {
      this.audio.unlock();
      unbindUnlock();
    });
    this.save = this.saveLoad.loadOrDefault();
    this.stage = this.makeMetaStage();
    this.attachVisibilitySave();
  }

  private attachVisibilitySave(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.saveLoad.save(this.save);
    });
  }

  private makeMetaStage(): Stage {
    const phase = new MetaPhase(this.save, this.input, this.audio, (s) => {
      this.save = s;
      this.saveLoad.save(s);
    });
    return { kind: 'meta', phase };
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    const loop = (t: number): void => {
      if (!this.running) return;
      const dtRaw = (t - this.last) / 1000;
      this.last = t;
      this.accum = Math.min(MAX_ACCUM, this.accum + dtRaw);
      while (this.accum >= FIXED_DT) {
        this.stage.phase.update(FIXED_DT);
        this.accum -= FIXED_DT;
      }
      this.advanceIfComplete();
      this.renderer.beginFrame();
      this.stage.phase.render(this.renderer.ctx);
      this.renderer.endFrame();
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }

  stop(): void {
    this.running = false;
  }

  private advanceIfComplete(): void {
    if (!this.stage.phase.isComplete()) return;
    const cur = this.stage;
    cur.phase.dispose?.();
    switch (cur.kind) {
      case 'meta':
        if ((cur.phase as MetaPhase).isStartingRun()) this.beginRun();
        else this.stage = this.makeMetaStage(); // shouldn't happen, but recover
        break;
      case 'heroRequest': {
        if (!this.run) {
          this.stage = this.makeMetaStage();
          return;
        }
        const def = WEAPONS[cur.hero.weaponPreference];
        // Lucky start: not implemented at the heat-subphase level yet, but
        // the modifier is plumbed through. Future task to hook into HeatSubphase.
        const forge = new ForgePhase(def, this.input, this.audio, this.modifiers);
        this.stage = { kind: 'forge', phase: forge, hero: cur.hero };
        break;
      }
      case 'forge': {
        if (!this.run) {
          this.stage = this.makeMetaStage();
          return;
        }
        const weapon = (cur.phase as ForgePhase).getWeapon();
        this.run.heroesServed += 1;
        this.save.stats.totalHeroesServed += 1;
        this.save.stats.totalWeaponsForged += 1;
        const enemy = generateEnemy(this.run);
        const combat = new CombatPhase(cur.hero, enemy, weapon, this.input, this.audio);
        this.stage = { kind: 'combat', phase: combat, hero: cur.hero, weapon, enemy };
        break;
      }
      case 'combat': {
        if (!this.run) {
          this.stage = this.makeMetaStage();
          return;
        }
        const outcome = (cur.phase as CombatPhase).getOutcome();
        if (!outcome) {
          this.stage = this.makeMetaStage();
          return;
        }
        this.applyOutcomeToRun(outcome, cur.hero, cur.enemy);
        this.stage = {
          kind: 'result',
          phase: new ResultPhase(outcome, cur.hero, this.input, this.audio),
          outcome,
          hero: cur.hero,
        };
        break;
      }
      case 'result': {
        if (!this.run) {
          this.stage = this.makeMetaStage();
          return;
        }
        // Decide next step.
        if (this.run.deaths >= 3) {
          this.endRun(false);
          return;
        }
        if (this.run.heroesServed >= MAX_HEROES_PER_RUN) {
          this.endRun(true);
          return;
        }
        if (cur.outcome.result === 'victory') {
          this.stage = { kind: 'shop', phase: new ShopPhase(this.run, this.input, this.audio) };
        } else {
          this.beginNextHero();
        }
        break;
      }
      case 'shop': {
        if (!this.run) {
          this.stage = this.makeMetaStage();
          return;
        }
        const pickedId = (cur.phase as ShopPhase).getPickedId();
        if (pickedId) applyUpgradeToContext(pickedId, { modifiers: this.modifiers });
        this.beginNextHero();
        break;
      }
      case 'gameOver':
        this.stage = this.makeMetaStage();
        this.saveLoad.save(this.save);
        break;
    }
  }

  private beginRun(): void {
    this.run = makeRunState();
    this.modifiers = makeForgeModifiers();
    // Apply permanent meta upgrades into modifiers.
    if (this.save.unlockedUpgrades.includes('apprentice_sharpener')) {
      this.modifiers.bonusDurability += 5;
    }
    this.beginNextHero();
  }

  private beginNextHero(): void {
    if (!this.run) {
      this.stage = this.makeMetaStage();
      return;
    }
    const hero = generateHero({ run: this.run, save: this.save });
    this.stage = {
      kind: 'heroRequest',
      phase: new HeroRequestPhase(hero, this.run, this.input, this.audio),
      hero,
    };
  }

  private applyOutcomeToRun(outcome: BattleOutcome, hero: Hero, enemy: Enemy): void {
    if (!this.run) return;
    let gold = outcome.goldEarned;
    if (this.save.unlockedUpgrades.includes('apprentice_carrier')) {
      gold = Math.round(gold * 1.1);
    }
    this.run.gold += gold;
    this.run.reputation = clampRep(this.run.reputation + outcome.reputationDelta);
    if (outcome.result === 'hero_death') {
      this.run.deaths += 1;
      this.save.stats.totalDeaths += 1;
      this.run.memorial.push({
        name: hero.name,
        title: hero.title,
        enemyType: enemy.type,
      });
      // Persist the memorial entry if the meta unlock is owned.
      if (this.save.unlockedUpgrades.includes('memorial_wall')) {
        this.save.memorial.push({
          name: hero.name,
          title: hero.title,
          enemyType: enemy.type,
          runIndex: this.save.stats.totalRunsCompleted + 1,
        });
      }
    } else if (outcome.result === 'victory') {
      this.save.stats.totalHeroesSaved += 1;
      if (outcome.weaponHallOfFameEligible) {
        this.run.hallOfFameWeapons.push(outcome.weapon);
        if (this.save.unlockedUpgrades.includes('hall_of_fame_display')) {
          this.save.hallOfFame.push(outcome.weapon);
        }
      }
    }
  }

  private endRun(completed: boolean): void {
    if (!this.run) {
      this.stage = this.makeMetaStage();
      return;
    }
    const heroesSaved =
      this.run.heroesServed - this.run.deaths -
      // Heroes whose weapon broke aren't deaths and aren't saved either.
      this.run.upgrades.length * 0; // placeholder; we only track deaths explicitly here.
    const renown = this.computeRenown(completed);
    this.save.renown += renown;
    if (this.save.renown > this.save.stats.highestRenown) {
      this.save.stats.highestRenown = this.save.renown;
    }
    if (completed) this.save.stats.totalRunsCompleted += 1;
    this.saveLoad.save(this.save);
    const summary: RunSummary = {
      heroesSaved: Math.max(0, heroesSaved),
      weaponsForged: this.run.heroesServed,
      hallOfFameCount: this.run.hallOfFameWeapons.length,
      goldEarned: this.run.gold,
      renownAwarded: renown,
      completed,
    };
    this.run = null;
    this.modifiers = makeForgeModifiers();
    this.stage = {
      kind: 'gameOver',
      phase: new GameOverPhase(summary, this.input, this.audio),
    };
  }

  private computeRenown(completed: boolean): number {
    if (!this.run) return 0;
    const saved = Math.max(0, this.run.heroesServed - this.run.deaths);
    let renown = saved * 1;
    renown += this.run.hallOfFameWeapons.length * 5;
    if (completed) renown += 10;
    return renown;
  }
}
